import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Plus, Edit, Trash2, Image as ImageIcon, Upload, Link, Loader2, GripVertical, Save } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminGallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [uploadMode, setUploadMode] = useState('file'); // 'file' or 'url'
  const [previewUrl, setPreviewUrl] = useState('');
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    src: '',
    category: 'events',
    title: '',
    description: ''
  });
  const { toast } = useToast();
  
  // Drag and drop state
  const [draggedItem, setDraggedItem] = useState(null);
  const [hasOrderChanged, setHasOrderChanged] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  
  // Bulk upload state
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });
  const bulkFileInputRef = useRef(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${API}/admin/gallery`, {
        headers: getAuthHeaders()
      });
      setImages(response.data);
      setHasOrderChanged(false);
    } catch (error) {
      console.error('Gallery fetch error:', error);
      toast({
        title: 'Error',
        description: 'Failed to load gallery images',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, image) => {
    setDraggedItem(image);
    e.dataTransfer.effectAllowed = 'move';
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedItem(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetImage) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.id === targetImage.id) return;

    const newImages = [...images];
    const draggedIndex = newImages.findIndex(img => img.id === draggedItem.id);
    const targetIndex = newImages.findIndex(img => img.id === targetImage.id);

    // Remove dragged item and insert at new position
    newImages.splice(draggedIndex, 1);
    newImages.splice(targetIndex, 0, draggedItem);

    setImages(newImages);
    setHasOrderChanged(true);
  };

  const saveOrder = async () => {
    setSavingOrder(true);
    try {
      const orderData = images.map((img, index) => ({
        id: img.id,
        order: index
      }));
      
      await axios.post(`${API}/admin/gallery/reorder`, orderData, {
        headers: getAuthHeaders()
      });
      
      setHasOrderChanged(false);
      toast({
        title: 'Success',
        description: 'Gallery order saved successfully'
      });
    } catch (error) {
      console.error('Error saving order:', error);
      toast({
        title: 'Error',
        description: 'Failed to save gallery order',
        variant: 'destructive'
      });
    } finally {
      setSavingOrder(false);
    }
  };

  // Bulk upload handler
  const handleBulkUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    // Validate all files
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const validFiles = files.filter(file => {
      if (!validTypes.includes(file.type)) {
        toast({
          title: 'Skipped',
          description: `${file.name} is not a valid image type`,
          variant: 'destructive'
        });
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: 'Skipped',
          description: `${file.name} is too large (max 10MB)`,
          variant: 'destructive'
        });
        return false;
      }
      return true;
    });

    if (!validFiles.length) return;

    setBulkUploading(true);
    setBulkProgress({ current: 0, total: validFiles.length });

    // Create FormData for bulk upload
    const formData = new FormData();
    validFiles.forEach(file => {
      formData.append('files', file);
    });

    try {
      const response = await axios.post(`${API}/uploads/images/bulk`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setBulkProgress(prev => ({ ...prev, percent: progress }));
        }
      });

      const { uploaded, errors, total_uploaded, total_errors } = response.data;

      // Create gallery entries for each uploaded image
      for (let i = 0; i < uploaded.length; i++) {
        const img = uploaded[i];
        setBulkProgress(prev => ({ ...prev, current: i + 1 }));
        
        await axios.post(`${API}/admin/gallery`, {
          src: img.url,
          category: 'events',
          title: img.original_name.replace(/\.[^/.]+$/, ''),
          description: `Uploaded on ${new Date().toLocaleDateString()}`
        }, {
          headers: getAuthHeaders()
        });
      }

      toast({
        title: 'Bulk Upload Complete',
        description: `${total_uploaded} images uploaded successfully${total_errors > 0 ? `, ${total_errors} failed` : ''}`
      });

      fetchImages();
      setShowBulkUpload(false);
    } catch (error) {
      console.error('Bulk upload error:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to upload images',
        variant: 'destructive'
      });
    } finally {
      setBulkUploading(false);
      setBulkProgress({ current: 0, total: 0 });
      if (bulkFileInputRef.current) {
        bulkFileInputRef.current.value = '';
      }
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast({
        title: 'Invalid File',
        description: 'Please select a valid image file (JPEG, PNG, GIF, or WebP)',
        variant: 'destructive'
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'File Too Large',
        description: 'Please select an image smaller than 5MB',
        variant: 'destructive'
      });
      return;
    }

    setUploading(true);
    
    try {
      // Convert to base64 and upload
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Data = reader.result;
        
        try {
          const response = await axios.post(`${API}/admin/upload-image`, {
            image_data: base64Data,
            filename: file.name
          }, {
            headers: {
              ...getAuthHeaders(),
              'Content-Type': 'application/json'
            }
          });
          
          setFormData({ ...formData, src: response.data.url });
          setPreviewUrl(response.data.url);
          toast({
            title: 'Success',
            description: 'Image uploaded successfully'
          });
        } catch (uploadError) {
          console.error('Upload error:', uploadError);
          // If upload endpoint doesn't exist, use base64 directly
          setFormData({ ...formData, src: base64Data });
          setPreviewUrl(base64Data);
          toast({
            title: 'Image Ready',
            description: 'Image processed successfully'
          });
        }
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('File processing error:', error);
      setUploading(false);
      toast({
        title: 'Error',
        description: 'Failed to process image',
        variant: 'destructive'
      });
    }
  };

  const handleUrlChange = (url) => {
    setFormData({ ...formData, src: url });
    setPreviewUrl(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.src) {
      toast({
        title: 'Error',
        description: 'Please upload an image or provide an image URL',
        variant: 'destructive'
      });
      return;
    }

    try {
      if (editingImage) {
        await axios.put(`${API}/admin/gallery/${editingImage.id}`, formData, {
          headers: getAuthHeaders()
        });
        toast({ title: 'Success', description: 'Image updated successfully' });
      } else {
        await axios.post(`${API}/admin/gallery`, formData, {
          headers: getAuthHeaders()
        });
        toast({ title: 'Success', description: 'Image added successfully' });
      }
      fetchImages();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save image',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    
    try {
      await axios.delete(`${API}/admin/gallery/${id}`, {
        headers: getAuthHeaders()
      });
      toast({ title: 'Success', description: 'Image deleted successfully' });
      fetchImages();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete image',
        variant: 'destructive'
      });
    }
  };

  const handleEdit = (image) => {
    setEditingImage(image);
    setFormData({
      src: image.src,
      category: image.category,
      title: image.title,
      description: image.description
    });
    setPreviewUrl(image.src);
    setUploadMode(image.src.startsWith('data:') ? 'file' : 'url');
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingImage(null);
    setFormData({
      src: '',
      category: 'events',
      title: '',
      description: ''
    });
    setPreviewUrl('');
    setUploadMode('file');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-gallery">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Gallery Management</h2>
          <p className="text-gray-600 mt-2">Manage your photo gallery ({images.length} images) - Drag to reorder</p>
        </div>
        <div className="flex gap-2">
          {hasOrderChanged && (
            <Button 
              onClick={saveOrder} 
              disabled={savingOrder}
              className="bg-green-500 hover:bg-green-600"
              data-testid="save-order-btn"
            >
              {savingOrder ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Order
            </Button>
          )}
          <Button 
            onClick={() => setShowBulkUpload(!showBulkUpload)}
            variant="outline"
            className="border-amber-500 text-amber-600 hover:bg-amber-50"
            data-testid="bulk-upload-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Bulk Upload
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-amber-500 hover:bg-amber-600" data-testid="add-image-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Image
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{editingImage ? 'Edit Image' : 'Add New Image'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Upload Mode Toggle */}
                <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
                  <button
                    type="button"
                    onClick={() => setUploadMode('file')}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                      uploadMode === 'file' 
                        ? 'bg-white shadow text-amber-600' 
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Upload className="w-4 h-4 inline mr-2" />
                    Upload File
                  </button>
                  <button
                    type="button"
                    onClick={() => setUploadMode('url')}
                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                      uploadMode === 'url' 
                        ? 'bg-white shadow text-amber-600' 
                        : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Link className="w-4 h-4 inline mr-2" />
                  Image URL
                </button>
              </div>

              {/* File Upload */}
              {uploadMode === 'file' && (
                <div className="space-y-2">
                  <Label>Upload Image (JPEG, PNG, GIF, WebP - Max 5MB)</Label>
                  <div 
                    className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-amber-500 transition-colors cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {uploading ? (
                      <div className="flex flex-col items-center">
                        <Loader2 className="w-10 h-10 text-amber-500 animate-spin mb-2" />
                        <p className="text-gray-600">Processing image...</p>
                      </div>
                    ) : previewUrl && uploadMode === 'file' ? (
                      <div className="space-y-2">
                        <img 
                          src={previewUrl} 
                          alt="Preview" 
                          className="max-h-40 mx-auto rounded-lg object-contain"
                        />
                        <p className="text-sm text-gray-500">Click to change image</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <Upload className="w-10 h-10 text-gray-400 mb-2" />
                        <p className="text-gray-600">Click to upload or drag and drop</p>
                        <p className="text-sm text-gray-400">JPEG, PNG, GIF, WebP up to 5MB</p>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      onChange={handleFileSelect}
                      className="hidden"
                      data-testid="file-input"
                    />
                  </div>
                </div>
              )}

              {/* URL Input */}
              {uploadMode === 'url' && (
                <div className="space-y-2">
                  <Label htmlFor="src">Image URL *</Label>
                  <Input
                    id="src"
                    value={formData.src.startsWith('data:') ? '' : formData.src}
                    onChange={(e) => handleUrlChange(e.target.value)}
                    placeholder="https://example.com/image.jpg"
                    data-testid="url-input"
                  />
                  {previewUrl && !previewUrl.startsWith('data:') && (
                    <div className="mt-2">
                      <img 
                        src={previewUrl} 
                        alt="Preview" 
                        className="max-h-40 rounded-lg object-contain"
                        onError={() => setPreviewUrl('')}
                      />
                    </div>
                  )}
                </div>
              )}

              <div>
                <Label htmlFor="category">Category *</Label>
                <select
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                  data-testid="category-select"
                >
                  <option value="events">Company Events</option>
                  <option value="community">Community Activities</option>
                  <option value="celebrations">Office Celebrations</option>
                </select>
              </div>
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="title-input"
                />
              </div>
              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  required
                  data-testid="description-input"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-amber-500 hover:bg-amber-600"
                  disabled={uploading || !formData.src}
                  data-testid="submit-btn"
                >
                  {editingImage ? 'Update' : 'Add'} Image
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Bulk Upload Panel */}
      {showBulkUpload && (
        <Card className="p-6 border-2 border-amber-200 bg-amber-50">
          <CardContent className="p-0">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-bold text-lg text-gray-900">Bulk Upload Images</h3>
                <p className="text-sm text-gray-600">Upload multiple images at once (max 20 images, 10MB each)</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowBulkUpload(false)}
              >
                Close
              </Button>
            </div>
            
            {bulkUploading ? (
              <div className="text-center py-8">
                <Loader2 className="w-12 h-12 text-amber-500 animate-spin mx-auto mb-4" />
                <p className="text-lg font-medium">
                  Uploading {bulkProgress.current} of {bulkProgress.total} images...
                </p>
                <p className="text-sm text-gray-500 mt-2">Please wait, this may take a moment</p>
              </div>
            ) : (
              <div
                className="border-2 border-dashed border-amber-300 rounded-lg p-8 text-center hover:border-amber-500 transition-colors cursor-pointer bg-white"
                onClick={() => bulkFileInputRef.current?.click()}
              >
                <Upload className="w-12 h-12 text-amber-400 mx-auto mb-3" />
                <p className="text-gray-700 font-medium">Click to select multiple images</p>
                <p className="text-sm text-gray-500 mt-1">JPEG, PNG, GIF, WebP supported</p>
              </div>
            )}
            
            <input
              ref={bulkFileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              multiple
              onChange={handleBulkUpload}
              className="hidden"
              data-testid="bulk-file-input"
            />
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
        </div>
      ) : images.length === 0 ? (
        <Card className="p-12 text-center">
          <CardContent>
            <ImageIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">No Images Yet</h3>
            <p className="text-gray-600 mb-4">Start by adding your first gallery image</p>
            <Button onClick={() => setIsDialogOpen(true)} className="bg-amber-500 hover:bg-amber-600">
              <Plus className="w-4 h-4 mr-2" />
              Add First Image
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {images.map((image, index) => (
            <Card 
              key={image.id} 
              className={`overflow-hidden hover:shadow-lg transition-all cursor-move ${
                draggedItem?.id === image.id ? 'opacity-50 scale-95' : ''
              }`}
              data-testid={`gallery-image-${image.id}`}
              draggable
              onDragStart={(e) => handleDragStart(e, image)}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, image)}
            >
              <div className="relative h-48 bg-gray-100">
                {/* Drag handle indicator */}
                <div className="absolute top-2 left-2 bg-white/80 rounded p-1 z-10">
                  <GripVertical className="w-4 h-4 text-gray-500" />
                </div>
                <img
                  src={image.src}
                  alt={image.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23eee" width="100" height="100"/><text fill="%23999" x="50%" y="50%" text-anchor="middle" dy=".3em">No Image</text></svg>';
                  }}
                />
                <div className="absolute top-2 right-2 flex gap-2">
                  <Button
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); handleEdit(image); }}
                    className="bg-blue-500 hover:bg-blue-600"
                    data-testid={`edit-${image.id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); handleDelete(image.id); }}
                    className="bg-red-500 hover:bg-red-600"
                    data-testid={`delete-${image.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                {/* Order number badge */}
                <div className="absolute bottom-2 left-2 bg-amber-500 text-white text-xs font-bold px-2 py-1 rounded">
                  #{index + 1}
                </div>
              </div>
              <CardContent className="p-4">
                <div className="mb-2">
                  <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                    {image.category === 'events' ? 'Company Events' : 
                     image.category === 'community' ? 'Community Activities' : 
                     'Office Celebrations'}
                  </span>
                </div>
                <h3 className="font-bold text-gray-900 mb-1">{image.title}</h3>
                <p className="text-sm text-gray-600 line-clamp-2">{image.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminGallery;
